/**
 * @fileoverview Configurações de temas da aplicação VikanaWeb.
 * Define múltiplos temas para acessibilidade: padrão, alto contraste, texto grande,
 * preto e branco, e texto destacado.
 *
 * Ajuste visual: mantém a identidade suave da marca em superfícies e navegação,
 * mas usa CTAs mais fortes e contrastantes para estimular clique e compra.
 *
 * @module Theme
 * @since 1.0.0
 */

import { createTheme } from '@mui/material/styles';

const BRAND_PRIMARY = '#E85D04';
const BRAND_PRIMARY_HOVER = '#C2410C';
const BRAND_PRIMARY_ACTIVE = '#9A3412';
const BRAND_SECONDARY = '#5F9EA0';
const BRAND_SECONDARY_LIGHT = '#A2D4DC';
const SURFACE_DEFAULT = 'rgba(107, 56, 54, 0.23)';
const SURFACE_PAPER = '#FFFFFF';
const TEXT_PRIMARY = '#2F3640';
const TEXT_SECONDARY = '#5D6D7E';
const HEADER_BG = 'rgba(107, 56, 54, 0.23)';
const HEADER_COLOR = '#FFFFFF';

/**
 * Configuração base compartilhada entre todos os temas.
 * Define tipografia, componentes customizados e estilos comuns.
 *
 * @type {Object}
 */
const baseTheme = {
  typography: {
    fontFamily: 'Arial',
    allVariants: {
      fontFamily: 'Arial',
    },
    button: {
      fontWeight: 700,
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
  },
  custom: {
    searchBar: {
      backgroundColor: '#FFFFFF',
      placeholderColor: '#9E9E9E',
      borderColor: '#E0E0E0',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '24px',
          padding: '10px 24px',
          fontSize: '1.02rem',
          fontWeight: 700,
          textTransform: 'none',
          transition: 'all 0.2s ease',
          '&:focus': {
            outline: 'none',
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          backgroundColor: BRAND_PRIMARY,
          color: '#FFFFFF',
          boxShadow: '0 8px 20px rgba(232, 93, 4, 0.22)',
          '&:hover': {
            backgroundColor: BRAND_PRIMARY_HOVER,
            transform: 'translateY(-1px)',
            boxShadow: '0 10px 24px rgba(194, 65, 12, 0.28)',
          },
          '&:active': {
            backgroundColor: BRAND_PRIMARY_ACTIVE,
            transform: 'translateY(0)',
            boxShadow: '0 6px 16px rgba(154, 52, 18, 0.24)',
          },
        },
        containedSecondary: {
          backgroundColor: BRAND_SECONDARY,
          color: '#FFFFFF',
          boxShadow: '0 6px 16px rgba(95, 158, 160, 0.18)',
          '&:hover': {
            backgroundColor: '#4D8789',
            boxShadow: '0 8px 20px rgba(77, 135, 137, 0.22)',
          },
        },
        outlinedPrimary: {
          borderWidth: '2px',
          borderColor: BRAND_PRIMARY,
          color: BRAND_PRIMARY,
          '&:hover': {
            borderWidth: '2px',
            backgroundColor: 'rgba(232, 93, 4, 0.06)',
            borderColor: BRAND_PRIMARY_HOVER,
          },
        },
        outlinedSecondary: {
          borderWidth: '2px',
          borderColor: BRAND_SECONDARY,
          color: BRAND_SECONDARY,
          '&:hover': {
            borderWidth: '2px',
            backgroundColor: 'rgba(95, 158, 160, 0.06)',
            borderColor: '#4D8789',
          },
        },
        textPrimary: {
          color: BRAND_PRIMARY,
          '&:hover': {
            backgroundColor: 'rgba(232, 93, 4, 0.06)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:focus': {
            outline: 'none',
            boxShadow: 'none',
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '1.02rem',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '1.02rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: SURFACE_PAPER,
          border: '1px solid rgba(95, 158, 160, 0.10)',
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          '&:hover': {
            boxShadow: '0 6px 24px rgba(95, 158, 160, 0.14)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          '&:focus': {
            outline: 'none',
          },
        },
      },
    },
  },
};

/**
 * Coleção de temas disponíveis na aplicação.
 * Cada tema é otimizado para diferentes necessidades de acessibilidade.
 *
 * @type {Object<string, import('@mui/material/styles').Theme>}
 */
export const themes = {
  default: createTheme({
    ...baseTheme,
    palette: {
      primary: {
        main: BRAND_PRIMARY,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: BRAND_SECONDARY,
        contrastText: '#FFFFFF',
      },
      background: {
        default: SURFACE_DEFAULT,
        paper: SURFACE_PAPER,
      },
      text: {
        primary: TEXT_PRIMARY,
        secondary: TEXT_SECONDARY,
      },
    },
    components: {
      ...baseTheme.components,
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: HEADER_BG,
            backdropFilter: 'blur(8px)',
            color: HEADER_COLOR,
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.10)',
          },
        },
      },
    },
  }),

  highContrast: createTheme({
    ...baseTheme,
    palette: {
      primary: {
        main: '#000000',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#FFFFFF',
        contrastText: '#000000',
      },
      background: {
        default: '#000000',
        paper: '#000000',
      },
      text: {
        primary: '#FFFFFF',
        secondary: '#FFFFFF',
      },
    },
    components: {
      ...baseTheme.components,
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: '#000000',
            color: '#FFFFFF',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          ...baseTheme.components.MuiButton.styleOverrides,
          containedPrimary: {
            backgroundColor: '#FFFFFF',
            color: '#000000',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#E0E0E0',
              transform: 'translateY(-1px)',
            },
          },
          outlinedPrimary: {
            borderWidth: '2px',
            borderColor: '#FFFFFF',
            color: '#FFFFFF',
            '&:hover': {
              borderWidth: '2px',
              backgroundColor: 'rgba(255,255,255,0.10)',
            },
          },
        },
      },
    },
  }),

  largeText: createTheme({
    ...baseTheme,
    typography: {
      ...baseTheme.typography,
      fontSize: 18,
      h1: { fontSize: '3rem' },
      h2: { fontSize: '2.5rem' },
      h3: { fontSize: '2rem' },
      h4: { fontSize: '1.75rem' },
      h5: { fontSize: '1.5rem' },
      h6: { fontSize: '1.25rem' },
      body1: { fontSize: '1.25rem' },
      body2: { fontSize: '1.125rem' },
      button: {
        ...baseTheme.typography.button,
        fontSize: '1.125rem',
      },
    },
    palette: {
      primary: {
        main: BRAND_PRIMARY,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: BRAND_SECONDARY,
        contrastText: '#FFFFFF',
      },
      background: {
        default: SURFACE_DEFAULT,
        paper: SURFACE_PAPER,
      },
      text: {
        primary: TEXT_PRIMARY,
        secondary: TEXT_SECONDARY,
      },
    },
    components: {
      ...baseTheme.components,
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: HEADER_BG,
            backdropFilter: 'blur(8px)',
            color: HEADER_COLOR,
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.10)',
          },
        },
      },
    },
  }),

  blackWhite: createTheme({
    ...baseTheme,
    palette: {
      primary: {
        main: '#FFFFFF',
        contrastText: '#000000',
      },
      secondary: {
        main: '#000000',
        contrastText: '#FFFFFF',
      },
      background: {
        default: '#FFFFFF',
        paper: '#FFFFFF',
      },
      text: {
        primary: '#000000',
        secondary: '#000000',
      },
    },
    components: {
      ...baseTheme.components,
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: '#FFFFFF',
            color: '#000000',
            border: '2px solid #000000',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: '#FFFFFF',
            border: '1px solid #000000',
            borderRadius: 8,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          ...baseTheme.components.MuiButton.styleOverrides,
          containedPrimary: {
            backgroundColor: '#000000',
            color: '#FFFFFF',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#222222',
            },
          },
          outlinedPrimary: {
            borderWidth: '2px',
            borderColor: '#000000',
            color: '#000000',
            '&:hover': {
              borderWidth: '2px',
              backgroundColor: 'rgba(0,0,0,0.06)',
            },
          },
        },
      },
    },
  }),

  highlightText: createTheme({
    ...baseTheme,
    palette: {
      primary: {
        main: BRAND_PRIMARY,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: BRAND_SECONDARY,
        contrastText: '#FFFFFF',
      },
      background: {
        default: SURFACE_DEFAULT,
        paper: SURFACE_PAPER,
      },
      text: {
        primary: TEXT_PRIMARY,
        secondary: TEXT_SECONDARY,
      },
    },
    components: {
      ...baseTheme.components,
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: HEADER_BG,
            backdropFilter: 'blur(8px)',
            color: HEADER_COLOR,
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.10)',
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
            fontWeight: 'bold',
          },
        },
      },
    },
  }),
};

/**
 * Tema padrão da aplicação.
 * @type {import('@mui/material/styles').Theme}
 */
export default themes.default;
